import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import CategoryTabs from "./components/CategoryTabs";
import TechnologyGrid from "./components/TechnologyGrid";
import LoadingSkeleton from "./components/LoadingSkeleton";
import ErrorState from "./components/ErrorState";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Zap, Layers } from "lucide-react";

const TechnologiesPage = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [technologies, setTechnologies] = useState([]);

  // Mock data for technologies (only Angular, Express, MongoDB, Elasticsearch logos updated)
  const technologiesData = [
    {
      id: "frontend",
      name: "Frontend",
      description: "Technologies that power our user interfaces and client-side applications",
      technologies: [
        {
          id: 1,
          name: "React",
          logo: "https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg",
          skillLevel: "expert",
          description: "A JavaScript library for building user interfaces"
        },
        {
          id: 2,
          name: "Angular",
          // updated -> official / Wikimedia copy (SVG)
          logo: "https://upload.wikimedia.org/wikipedia/commons/3/30/Angular_Logo.svg",
          skillLevel: "expert",
          description: "Platform for building mobile and desktop web applications"
        },
        {
          id: 3,
          name: "Vue.js",
          logo: "https://upload.wikimedia.org/wikipedia/commons/9/95/Vue.js_Logo_2.svg",
          skillLevel: "intermediate",
          description: "Progressive JavaScript framework for building UIs"
        },
        {
          id: 4,
          name: "TypeScript",
          logo: "https://upload.wikimedia.org/wikipedia/commons/4/4c/Typescript_logo_2020.svg",
          skillLevel: "expert",
          description: "Typed superset of JavaScript that compiles to plain JavaScript"
        },
        {
          id: 5,
          name: "Tailwind CSS",
          logo: "https://upload.wikimedia.org/wikipedia/commons/d/d5/Tailwind_CSS_Logo.svg",
          skillLevel: "expert",
          description: "Utility-first CSS framework for rapid UI development"
        },
        {
          id: 6,
          name: "Next.js",
          logo: "https://upload.wikimedia.org/wikipedia/commons/8/8e/Nextjs-logo.svg",
          skillLevel: "expert",
          description: "React framework for production-grade applications"
        }
      ]
    },
    {
      id: "backend",
      name: "Backend",
      description: "Technologies that power our server-side applications and APIs",
      technologies: [
        {
          id: 7,
          name: "Node.js",
          logo: "https://upload.wikimedia.org/wikipedia/commons/d/d9/Node.js_logo.svg",
          skillLevel: "expert",
          description: "JavaScript runtime built on Chrome's V8 JavaScript engine"
        },
        {
          id: 8,
          name: "Express",
          // updated -> reliable community/vector copy (SVG)
          logo: "https://www.vectorlogo.zone/logos/expressjs/expressjs-ar21.svg",
          skillLevel: "expert",
          description: "Fast, unopinionated, minimalist web framework for Node.js"
        },
        {
          id: 9,
          name: "Django",
          logo: "https://static.djangoproject.com/img/logos/django-logo-negative.svg",
          skillLevel: "intermediate",
          description: "High-level Python web framework that encourages rapid development"
        },
        {
          id: 10,
          name: "Spring Boot",
          logo: "https://upload.wikimedia.org/wikipedia/commons/7/79/Spring_Boot.svg",
          skillLevel: "intermediate",
          description: "Java-based framework for building web applications and microservices"
        },
        {
          id: 11,
          name: "Laravel",
          logo: "https://upload.wikimedia.org/wikipedia/commons/9/9a/Laravel.svg",
          skillLevel: "intermediate",
          description: "PHP web application framework with expressive, elegant syntax"
        },
        {
          id: 12,
          name: "ASP.NET Core",
          logo: "https://upload.wikimedia.org/wikipedia/commons/e/ee/.NET_Core_Logo.svg",
          skillLevel: "intermediate",
          description: "Cross-platform, high-performance framework for building modern cloud-based applications"
        }
      ]
    },
    {
      id: "cloud",
      name: "Cloud & DevOps",
      description: "Technologies that power our infrastructure and deployment pipelines",
      technologies: [
        {
          id: 13,
          name: "AWS",
          logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
          skillLevel: "expert",
          description: "Comprehensive cloud computing platform"
        },
        {
          id: 14,
          name: "Azure",
          logo: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Microsoft_Azure.svg",
          skillLevel: "intermediate",
          description: "Microsoft's cloud computing service for building, testing, deploying, and managing applications"
        },
        {
          id: 15,
          name: "Docker",
          logo: "https://upload.wikimedia.org/wikipedia/commons/4/4e/Docker_%28container_engine%29_logo.svg",
          skillLevel: "expert",
          description: "Platform for developing, shipping, and running applications in containers"
        },
        {
          id: 16,
          name: "Kubernetes",
          logo: "https://upload.wikimedia.org/wikipedia/commons/3/39/Kubernetes_logo_without_workmark.svg",
          skillLevel: "intermediate",
          description: "Open-source system for automating deployment, scaling, and management of containerized applications"
        },
        {
          id: 17,
          name: "Terraform",
          logo: "https://www.datocms-assets.com/2885/1620155116-brandhcterraformverticalcolor.svg",
          skillLevel: "intermediate",
          description: "Infrastructure as code software tool for building, changing, and versioning infrastructure safely and efficiently"
        },
        {
          id: 18,
          name: "GitHub Actions",
          logo: "https://github.githubassets.com/images/modules/site/features/actions-icon-actions.svg",
          skillLevel: "expert",
          description: "CI/CD platform that allows you to automate your build, test, and deployment pipeline"
        }
      ]
    },
    {
      id: "database",
      name: "Database & Storage",
      description: "Technologies that power our data storage and management systems",
      technologies: [
        {
          id: 19,
          name: "MongoDB",
          // updated -> Wikimedia Commons / official brand asset (SVG)
          logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/MongoDB_Logo.svg",
          skillLevel: "expert",
          description: "Document-oriented NoSQL database used for high volume data storage"
        },
        {
          id: 20,
          name: "PostgreSQL",
          logo: "https://upload.wikimedia.org/wikipedia/commons/2/29/Postgresql_elephant.svg",
          skillLevel: "expert",
          description: "Powerful, open source object-relational database system"
        },
        {
          id: 21,
          name: "MySQL",
          logo: "https://www.mysql.com/common/logos/logo-mysql-170x115.png",
          skillLevel: "expert",
          description: "Open-source relational database management system"
        },
        {
          id: 22,
          name: "Redis",
          logo: "https://upload.wikimedia.org/wikipedia/commons/6/64/Logo-redis.svg",
          skillLevel: "intermediate",
          description: "In-memory data structure store, used as a database, cache, and message broker"
        },
        {
          id: 23,
          name: "Elasticsearch",
          // updated -> Wikimedia Commons / official Elastic asset (SVG)
          logo: "https://upload.wikimedia.org/wikipedia/commons/f/f4/Elasticsearch_logo.svg",
          skillLevel: "intermediate",
          description: "Distributed, RESTful search and analytics engine"
        },
        {
          id: 24,
          name: "Firebase",
          logo: "https://firebase.google.com/downloads/brand-guidelines/PNG/logo-logomark.png",
          skillLevel: "expert",
          description: "Platform developed by Google for creating mobile and web applications"
        }
      ]
    }
  ];


  // Simulate data fetching
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        setTechnologies(technologiesData);
        setActiveCategory(technologiesData[0].id);
        setHasError(false);
      } catch (error) {
        console.error("Error fetching technologies:", error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRetry = () => {
    setHasError(false);
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setTechnologies(technologiesData);
      setActiveCategory(technologiesData[0].id);
      setIsLoading(false);
    }, 1500);
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  // Get category icon
  const getCategoryIcon = (categoryId) => {
    const icons = {
      frontend: Sparkles,
      backend: Zap,
      cloud: Layers,
      database: Layers
    };
    return icons[categoryId] || Layers;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 mt-10 overflow-x-hidden">
      <Header />

      <main id="main-content" className="flex-grow" tabIndex="-1">
        {/* Enhanced Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white py-20 3xl:py-32 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          </div>

          <div className="container mx-auto relative z-10 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl 3xl:max-w-5xl 4xl:max-w-6xl mx-auto text-center"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 3xl:px-6 3xl:py-3 rounded-full mb-6 3xl:mb-8 border border-white/20"
              >
                <motion.div
                  animate={{
                    rotate: 360
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  <Sparkles className="w-5 h-5 3xl:w-6 3xl:h-6 text-yellow-300" />
                </motion.div>
                <span className="text-sm 3xl:text-base font-bold uppercase tracking-wider">Cutting-Edge Technology</span>
              </motion.div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl 3xl:text-7xl font-bold mb-6 3xl:mb-8 leading-tight tracking-tight">
                Our Technology Stack
              </h1>
              <p className="text-lg md:text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl opacity-95 leading-relaxed max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto" style={{ lineHeight: '1.8' }}>
                We use modern, reliable and scalable technologies to build high-performance digital solutions. Our expertise across multiple platforms ensures we choose the right tools for every project delivering seamless, secure and future-ready experiences.
              </p>
            </motion.div>
          </div>


        </section>

        {isLoading ? (
          <LoadingSkeleton />
        ) : hasError ? (
          <ErrorState onRetry={handleRetry} />
        ) : (
          <div className="container mx-auto py-16 3xl:py-24 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            {/* Enhanced Category Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <CategoryTabs
                categories={technologies}
                activeCategory={activeCategory}
                onCategoryChange={handleCategoryChange}
              />
            </motion.div>

            {/* Enhanced Content Area */}
            <AnimatePresence mode="wait">
              {technologies.map((category) => (
                category.id === activeCategory && (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, x: 60, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -60, scale: 0.95 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.22, 1, 0.36, 1],
                      type: "spring",
                      stiffness: 200,
                      damping: 25
                    }}
                    className="mt-12 3xl:mt-16"
                  >
                    {/* Enhanced Category Header */}
                    <motion.div
                      className="mb-10 3xl:mb-14 relative"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                    >
                      <div className="flex items-start gap-4 3xl:gap-6">
                        {/* Icon Badge */}
                        <motion.div
                          className="flex-shrink-0 w-16 h-16 3xl:w-20 3xl:h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ type: "spring", stiffness: 400, damping: 15 }}
                        >
                          {React.createElement(getCategoryIcon(category.id), {
                            className: "w-8 h-8 3xl:w-10 3xl:h-10 text-white",
                            strokeWidth: 2.5
                          })}
                        </motion.div>

                        {/* Text Content */}
                        <div className="flex-grow">
                          <h2 className="text-3xl md:text-4xl 3xl:text-5xl font-bold text-gray-900 mb-3 3xl:mb-4 leading-tight tracking-tight">
                            {category.name}
                          </h2>
                          <p className="text-gray-600 text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl leading-relaxed" style={{ lineHeight: '1.7' }}>
                            {category.description}
                          </p>
                        </div>
                      </div>

                      {/* Decorative Line */}
                      <motion.div
                        className="absolute -bottom-4 left-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-transparent rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
                      />
                    </motion.div>

                    {/* Technology Grid with Stagger Animation */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      <TechnologyGrid technologies={category.technologies} />
                    </motion.div>

                    {/* Stats Section (Optional Enhancement) */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                      className="mt-12 3xl:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 3xl:gap-8"
                    >
                      <div className="bg-white rounded-2xl p-6 3xl:p-8 shadow-md border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 3xl:w-12 3xl:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <Zap className="w-5 h-5 3xl:w-6 3xl:h-6 text-blue-600" />
                          </div>
                          <h3 className="text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl font-bold text-gray-900">
                            {category.technologies.filter(t => t.skillLevel === 'expert').length}
                          </h3>
                        </div>
                        <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-600 font-medium">Expert Level Technologies</p>
                      </div>

                      <div className="bg-white rounded-2xl p-6 3xl:p-8 shadow-md border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 3xl:w-12 3xl:h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                            <Layers className="w-5 h-5 3xl:w-6 3xl:h-6 text-purple-600" />
                          </div>
                          <h3 className="text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl font-bold text-gray-900">
                            {category.technologies.length}
                          </h3>
                        </div>
                        <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-600 font-medium">Total Technologies</p>
                      </div>

                      <div className="bg-white rounded-2xl p-6 3xl:p-8 shadow-md border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 3xl:w-12 3xl:h-12 rounded-xl bg-green-100 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 3xl:w-6 3xl:h-6 text-green-600" />
                          </div>
                          <h3 className="text-2xl 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl font-bold text-gray-900">
                            {Math.round((category.technologies.filter(t => t.skillLevel === 'expert').length / category.technologies.length) * 100)}%
                          </h3>
                        </div>
                        <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-600 font-medium">Expertise Rate</p>
                      </div>
                    </motion.div>
                  </motion.div>
                )
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default TechnologiesPage;
